'use client';

import { Button } from '@shared/components/shadcn';
import { twMerge } from '@shared/themes';
import type { Currency } from '@shared/utils';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import type { ComponentProps } from 'react';
import { createPortal } from 'react-dom';
import { DecorationShine } from '../../DecorationShine';
import { useLotteryNextDraw } from '../../lotteries/hooks';
import { JackpotLabel } from '../../lotteries/JackpotLabel/JackpotLabel';
import { DecorationFlower } from '../../LotteryHeaderContainer/components';
import { Coupon } from '../../svg/Coupon/Coupon';
import type { LotteryName } from '../types';

type StickyPurchaseBarProps = ComponentProps<'div'> & {
  lotteryName: LotteryName;
  currency: Currency;
  betUrl: string;
};

function StickyPurchaseBar({ lotteryName, currency, betUrl, className, ...props }: StickyPurchaseBarProps) {
  const t = useTranslations('lotteries.purchase.bar');
  const { data, isLoading } = useLotteryNextDraw(lotteryName);
  const nextDraw = data?.find(draw => draw.lotteryName === lotteryName);

  if (isLoading || !nextDraw) return null;

  const jackpot = nextDraw.jackpot.regular[currency];
  if (jackpot == null) return null;

  const drawingDate = new Date(nextDraw.drawingDate);
  const jackpotText = t('title', { date: drawingDate });
  const zeroJackpotLabelClass = jackpot === 0 ? 'max-sm:!text-base' : undefined;

  const lotteryTextClass = `text-${lotteryName.toLowerCase()}`;

  const classes = twMerge('fixed bottom-0 left-0 right-0 z-50 flex items-center gap-0 w-full rounded-t-lg shadow-top-m isolate overflow-hidden bg-background', className);

  return createPortal(
    <div className={classes} data-testid="sticky-purchase-bar" {...props}>
      <div className="flex grow items-center justify-between relative z-10">
        <div className="flex flex-col items-start pl-5 py-3.5 flex-1 min-w-0 rounded-tl-lg">
          <p className="paragraph-s text-foreground">{jackpotText}</p>
          <JackpotLabel namespace={`lotteries.${lotteryName}`} jackpot={jackpot} jackpotLabelSize="small" className={zeroJackpotLabelClass} />
        </div>

        <div className={`relative flex items-center gap-4 shrink-0 pr-5 py-3.5 overflow-hidden`}>
          <div className={`w-16 h-10 flex items-top justify-center shrink-0 z-2`}>
            <DecorationShine className={`w-5 h-5 ${lotteryTextClass} align-top pr-px`} aria-hidden="true" />
            <Coupon />
          </div>
          <Button asChild variant="primary" size="m" className="relative z-2">
            <Link href={betUrl} data-track-name="Bet">
              {t('place_bet')}
            </Link>
          </Button>
        </div>
        <DecorationFlower className={`absolute w-47.5 h-auto -right-12.5 -bottom-12.5 z-1 ${lotteryTextClass} rotate-110`} aria-hidden="true" />
      </div>
    </div>,
    document.body
  );
}

export { StickyPurchaseBar };
export type { StickyPurchaseBarProps };
